if (!customElements.get("product-parts")) {
  class ProductParts extends HTMLElement {
    constructor() {
      super();

      this.data = JSON.parse(
        this.querySelector(".productPartsDetails").innerHTML,
      );

      this.mainModels = (Array.isArray(this.data.productModels)
        ? this.data.productModels
        : []
      ).map((model) => {
        if (!model?.variant) return model;

        const variant = { ...model.variant };
        if (typeof variant.available !== "boolean") {
          variant.available = model.available === true;
        }

        return { ...model, variant };
      });

      this.mainVariants = this.mainModels
        .map((model) => model.variant)
        .filter(Boolean);

      this.prices = this.data.prices || {};
      this.variant = this.data.selectedVariant || null;
      this.parts = [...this.querySelectorAll(".product-part")];
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form");
      this.atcButton = this.productForm.querySelector('[name="add"]');

      this.noStockText =
        this.dataset.noStockText ||
        this.productForm?.querySelector("[data-no-stock-text]")?.dataset.noStockText ||
        "De retour bientôt";

      this.addToCartText =
        this.atcButton?.dataset.addToCartText ||
        this.atcButton?.textContent?.trim() ||
        "";
      this.lastInteractedPartIndex = 0;

      this.addEventListener("change", this.handleChange.bind(this));
      this.addEventListener("click", this.handleClick.bind(this));

      window.initLazyScript(this, this.initVariant.bind(this));

      setTimeout(() => {
        this.updateSaleBadges();
      }, 100);
    }

    normalizeValue(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    }

    normalizeSku(value) {
      return String(value || "")
        .split("+")
        .map((part) => this.normalizeValue(part))
        .filter(Boolean)
        .join("+");
    }

    getVariantEventDetail() {
      return {
        form: this.productForm,
        variant: this.variant,
        allVariants: this.mainVariants,
        selectedOptions: this.variant?.options || [],
      };
    }

    setAddToCartState(variant) {
      if (!this.atcButton) return;

      const variantAvailable = variant?.available === true;

      this.atcButton.disabled = !variantAvailable;
      this.atcButton.textContent = variantAvailable
        ? this.addToCartText
        : this.noStockText;
      this.atcButton.classList.toggle("is-disabled", !variantAvailable);

      if (variantAvailable) {
        this.atcButton.removeAttribute("aria-disabled");
      } else {
        this.atcButton.setAttribute("aria-disabled", "true");
      }
    }

    updateVariantInputs() {
      if (!this.section || !this.variant?.id) return;

      this.section
        .querySelectorAll(".js-product-form, .js-instalments-form")
        .forEach((form) => {
          const input = form.querySelector('[name="id"]');
          if (!input) return;

          input.value = this.variant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
    }

    getPietementPartIndex() {
      return 0;
    }

    isPietementPart(partIndex) {
      return partIndex === this.getPietementPartIndex();
    }

    isPlateauPart(partIndex) {
      return !this.isPietementPart(partIndex);
    }

    getSaleText(variant, mode = "percent") {
      if (!variant || !variant.compare_at_price || !variant.price) return null;

      const price = Number(variant.price);
      const compare = Number(variant.compare_at_price);

      if (!compare || compare <= price) return null;

      if (mode === "label") {
        return "Promo";
      }

      const percent = Math.max(1, Math.round((1 - price / compare) * 100));

      return `-${percent}%`;
    }

    getSaleScore(variant) {
      if (!variant || !variant.compare_at_price || !variant.price) return 0;

      const price = Number(variant.price);
      const compare = Number(variant.compare_at_price);

      if (!compare || compare <= price) return 0;

      return compare - price;
    }

    isSaleVariant(variant) {
      return Boolean(this.getSaleText(variant));
    }

    getSaleVariants() {
      return this.mainVariants.filter((variant) => this.isSaleVariant(variant));
    }

    getPartVariantsFromMainVariant(mainVariant) {
      if (!mainVariant?.sku) return null;

      const skuParts = String(mainVariant.sku)
        .split("+")
        .map((sku) => this.normalizeValue(sku))
        .filter(Boolean);

      if (!skuParts.length) return null;

      const matchedPartVariants = [];

      this.parts.forEach((part, partIndex) => {
        const partVariants = this.data.productParts[partIndex]?.variants || [];

        const matchedPartVariant = partVariants.find((partVariant) => {
          return skuParts.includes(this.normalizeValue(partVariant.sku));
        });

        if (matchedPartVariant) {
          matchedPartVariants[partIndex] = matchedPartVariant;
        }
      });

      if (matchedPartVariants.filter(Boolean).length < this.parts.length) {
        return null;
      }

      return matchedPartVariants;
    }

    getBestSaleVariantForPietementOption(optionIndex, optionValue) {
      const pietementPartIndex = this.getPietementPartIndex();
      const pietementPart = this.parts[pietementPartIndex];

      const selectedPietementOptions = pietementPart
        ? this.getPartSelectedOptions(pietementPart)
        : [];

      let bestSaleVariant = null;
      let bestSaleScore = -1;

      this.getSaleVariants().forEach((saleVariant) => {
        const partVariants = this.getPartVariantsFromMainVariant(saleVariant);
        if (!partVariants) return;

        const pietementVariant = partVariants[pietementPartIndex];
        if (!pietementVariant?.options) return;

        const testedOption = pietementVariant.options[optionIndex];

        if (
          this.normalizeValue(testedOption) !== this.normalizeValue(optionValue)
        ) {
          return;
        }

        const isPietementTypeOption = optionIndex === 1;

        if (!isPietementTypeOption) {
          const isCompatibleWithSelectedPietementType =
            pietementVariant.options.every((saleOptionValue, saleOptionIndex) => {
              if (saleOptionIndex === optionIndex) {
                return true;
              }

              if (saleOptionIndex < optionIndex) {
                return true;
              }

              const selectedOptionValue = selectedPietementOptions[saleOptionIndex];

              if (!selectedOptionValue) return true;

              return (
                this.normalizeValue(saleOptionValue) ===
                this.normalizeValue(selectedOptionValue)
              );
            });

          if (!isCompatibleWithSelectedPietementType) return;
        }

        const score = this.getSaleScore(saleVariant);

        if (score > bestSaleScore) {
          bestSaleScore = score;
          bestSaleVariant = saleVariant;
        }
      });

      return bestSaleVariant;
    }

    getSelectedPietementVariant() {
      const pietementPartIndex = this.getPietementPartIndex();
      const pietementPart = this.parts[pietementPartIndex];

      if (!pietementPart) return null;

      const selectedOptions = this.getPartSelectedOptions(pietementPart);

      return this.findPartVariantByOptions(
        pietementPartIndex,
        selectedOptions,
      );
    }

    getBestSaleVariantForPlateauOption(partIndex, optionIndex, optionValue) {
      const selectedPietementVariant = this.getSelectedPietementVariant();

      if (!selectedPietementVariant?.sku) return null;

      const selectedPietementSku = this.normalizeValue(
        selectedPietementVariant.sku,
      );

      const plateauPart = this.parts[partIndex];
      const selectedPlateauOptions = plateauPart
        ? this.getPartSelectedOptions(plateauPart)
        : [];

      let bestSaleVariant = null;
      let bestSaleScore = -1;

      this.getSaleVariants().forEach((saleVariant) => {
        const partVariants = this.getPartVariantsFromMainVariant(saleVariant);
        if (!partVariants) return;

        const salePietementVariant = partVariants[this.getPietementPartIndex()];
        const salePlateauVariant = partVariants[partIndex];

        if (!salePietementVariant?.sku || !salePlateauVariant?.options) return;

        if (
          this.normalizeValue(salePietementVariant.sku) !== selectedPietementSku
        ) {
          return;
        }

        const testedOption = salePlateauVariant.options[optionIndex];

        if (
          this.normalizeValue(testedOption) !== this.normalizeValue(optionValue)
        ) {
          return;
        }

        const isPlateauColorOption = optionIndex === 0;

        if (!isPlateauColorOption) {
          const isCompatibleWithSelectedPlateauColor =
            salePlateauVariant.options.every((saleOptionValue, saleOptionIndex) => {
              if (saleOptionIndex === optionIndex) {
                return true;
              }

              if (saleOptionIndex > optionIndex) {
                return true;
              }

              const selectedOptionValue = selectedPlateauOptions[saleOptionIndex];

              if (!selectedOptionValue) return true;

              return (
                this.normalizeValue(saleOptionValue) ===
                this.normalizeValue(selectedOptionValue)
              );
            });

          if (!isCompatibleWithSelectedPlateauColor) return;
        }

        const score = this.getSaleScore(saleVariant);

        if (score > bestSaleScore) {
          bestSaleScore = score;
          bestSaleVariant = saleVariant;
        }
      });

      return bestSaleVariant;
    }

    isOptionSelected(optionEl) {
      return (
        optionEl.classList.contains("is-selected") ||
        optionEl.getAttribute("aria-selected") === "true"
      );
    }

    clearSaleBadge(optionEl) {
      optionEl.classList.remove(
        "product-part-option--has-sale",
        "product-part-option--has-sale-active",
        "product-part-option--has-sale-muted",
      );

      const existingBadge = optionEl.querySelector(".product-part-sale-badge");
      if (existingBadge) existingBadge.remove();
    }

    addSaleBadge(optionEl, saleText, state = "muted") {
      this.clearSaleBadge(optionEl);

      if (!saleText) return;

      optionEl.classList.add("product-part-option--has-sale");

      if (state === "active") {
        optionEl.classList.add("product-part-option--has-sale-active");
      } else {
        optionEl.classList.add("product-part-option--has-sale-muted");
      }

      const badge = document.createElement("span");
      badge.className = "product-part-sale-badge";
      badge.innerHTML = `<span>${saleText}</span>`;

      if (
        optionEl.classList.contains("product-part-size-button") ||
        optionEl.classList.contains("product-part-drawer-button")
      ) {
        badge.classList.add("product-part-sale-badge--size");
      }

      const label =
        optionEl.querySelector(".product-part-size-button__label") ||
        optionEl.querySelector(".product-part-drawer-button__label") ||
        optionEl.querySelector(".product-part-swatch__label") ||
        optionEl.querySelector(".product-part-type-swatch__label");

      if (label) {
        optionEl.insertBefore(badge, label);
      } else {
        optionEl.appendChild(badge);
      }
    }

    updateSaleBadges() {
      const lastPartIndex = this.parts.length - 1;

      this.parts.forEach((part, partIndex) => {
        const selectors = [...part.querySelectorAll(".option-selector")];
        const lastOptionIndex = selectors.length - 1;

        selectors.forEach((selector, optionIndex) => {
          const options = [...selector.querySelectorAll(".js-option")];

          options.forEach((optionEl) => {
            const value = optionEl.dataset.value;

            this.clearSaleBadge(optionEl);

            if (!value) return;

            let saleVariant = null;

            if (this.isPietementPart(partIndex)) {
              saleVariant = this.getBestSaleVariantForPietementOption(
                optionIndex,
                value,
              );
            }

            if (this.isPlateauPart(partIndex)) {
              saleVariant = this.getBestSaleVariantForPlateauOption(
                partIndex,
                optionIndex,
                value,
              );
            }

            if (!saleVariant) return;

            const isLastOptionOfLastPart =
              partIndex === lastPartIndex && optionIndex === lastOptionIndex;

            const saleText = isLastOptionOfLastPart
              ? this.getSaleText(saleVariant, "percent")
              : this.getSaleText(saleVariant, "label");

            if (!saleText) return;

            const state = this.isOptionSelected(optionEl) ? "active" : "muted";

            this.addSaleBadge(optionEl, saleText, state);
          });
        });
      });
    }

    findPartVariantByOptions(partIndex, selectedOptions) {
      const variants = this.data.productParts[partIndex]?.variants || [];
      const normalizedSelected = selectedOptions.map((value) =>
        this.normalizeValue(value),
      );

      return variants.find((variant) => {
        if (
          !variant?.options ||
          variant.options.length !== normalizedSelected.length
        ) {
          return false;
        }

        return variant.options.every((optionValue, index) => {
          return this.normalizeValue(optionValue) === normalizedSelected[index];
        });
      });
    }

    findMainVariantByModel(modelName) {
      const normalizedModel = this.normalizeSku(modelName);

      const matchedModel = this.mainModels.find((model) => {
        return this.normalizeSku(model.name) === normalizedModel;
      });

      if (!matchedModel || !matchedModel.variant?.id) {
        return null;
      }

      return matchedModel.variant;
    }

    findMainVariantFromPartOptions(partOptions) {
      const skuParts = [];

      this.parts.forEach((part, partIndex) => {
        const options = partOptions[partIndex] || [];
        const partVariant = this.findPartVariantByOptions(partIndex, options);

        if (partVariant && partVariant.sku) {
          skuParts.push(this.normalizeValue(partVariant.sku));

          if (this.dataset.isDuo === "true" && partIndex === 1) {
            skuParts.push(this.normalizeValue(partVariant.sku));
          }
        }
      });

      if (skuParts.length < this.parts.length) return null;

      const normalizedSku = this.normalizeSku(skuParts.join("+"));
      return this.findMainVariantByModel(normalizedSku);
    }

    getCurrentPriceData() {
      if (!this.variant?.id) return null;
      return this.prices[String(this.variant.id)] || null;
    }

    updateDisplayedPrice() {
      const priceData = this.getCurrentPriceData();
      if (!priceData) return;

      const priceEl = document.querySelector("[data-product-price]");
      const compareEl = document.querySelector("[data-product-compare-price]");
      const priceWrapper = document.querySelector(
        ".product-info__price .price, .price-container .price",
      );

      const isOnSale = Boolean(
        priceData.compareAtPrice ||
          (this.variant?.compare_at_price &&
            Number(this.variant.compare_at_price) > Number(this.variant.price)),
      );

      if (priceWrapper) {
        priceWrapper.classList.toggle("price--on-sale", isOnSale);
      }

      if (priceEl) {
        priceEl.innerHTML = priceData.price || "";
      }

      if (compareEl) {
        if (isOnSale && priceData.compareAtPrice) {
          compareEl.innerHTML = priceData.compareAtPrice;
          compareEl.removeAttribute("hidden");
          compareEl.style.display = "";
        } else {
          compareEl.innerHTML = "";
          compareEl.setAttribute("hidden", "hidden");
          compareEl.style.display = "none";
        }
      }
    }

    initVariant() {
      if (!this.variant || !this.variant.sku) return;

      const skuParts = this.variant.sku.split("+").map((sku) => sku.trim());

      this.parts.forEach((part, n) => {
        const partVariants = this.data.productParts[n]?.variants || [];

        const matchedSku = skuParts.find((sku) =>
          partVariants.some(
            (v) => this.normalizeValue(v.sku) === this.normalizeValue(sku),
          ),
        );

        if (!matchedSku) {
          console.warn(
            `Aucun SKU correspondant trouvé pour la part ${n}`,
            skuParts,
          );
          return;
        }

        const partVariant = partVariants.find(
          (v) => this.normalizeValue(v.sku) === this.normalizeValue(matchedSku),
        );

        if (!partVariant) return;

        const selectors = [...part.querySelectorAll(".option-selector")];

        selectors.forEach((selector, m) => {
          const optionValue = partVariant.options[m];

          const customSelect = selector.querySelector("custom-select");
          if (customSelect) {
            const option = customSelect.querySelector(
              `.custom-select__option[data-value="${CSS.escape(optionValue)}"]`,
            );

            if (option) {
              customSelect.selectOption(option, false);
            }
            return;
          }

          const swatchGroup = selector.querySelector(".product-part-swatches");
          if (swatchGroup) {
            this.selectSwatch(swatchGroup, optionValue, false);
            return;
          }

          const typeGroup = selector.querySelector(
            ".product-part-type-swatches",
          );
          if (typeGroup) {
            this.selectTypeSwatch(typeGroup, optionValue);
            return;
          }

          const sizeGroup = selector.querySelector(
            ".product-part-size-buttons",
          );
          if (sizeGroup) {
            this.selectSizeButton(sizeGroup, optionValue);
            return;
          }

          const drawerGroup = selector.querySelector(
            ".product-part-drawer-buttons",
          );
          if (drawerGroup) {
            this.selectDrawerButton(drawerGroup, optionValue);
          }
        });
      });

      this.updateOptionAvailability();
      this.updateSaleBadges();
      this.updateDisplayedPrice();
      this.setAddToCartState(this.variant);
      this.onVariantChange();
    }

    handleClick(event) {
      const part = event.target.closest(".product-part");
      if (part) {
        this.lastInteractedPartIndex = this.parts.indexOf(part);
      }

      const swatch = event.target.closest(".product-part-swatch");
      if (swatch) {
        const group = swatch.closest(".product-part-swatches");
        if (!group) return;

        this.selectSwatch(group, swatch.dataset.value, true);
        this.updateOptionAvailability();
        this.handleChange();
        return;
      }

      const typeSwatch = event.target.closest(".product-part-type-swatch");
      if (typeSwatch) {
        const group = typeSwatch.closest(".product-part-type-swatches");
        if (!group) return;

        this.selectTypeSwatch(group, typeSwatch.dataset.value);
        this.updateOptionAvailability();
        this.handleChange();
        return;
      }

      const sizeButton = event.target.closest(".product-part-size-button");
      if (sizeButton) {
        const group = sizeButton.closest(".product-part-size-buttons");
        if (!group) return;

        this.selectSizeButton(group, sizeButton.dataset.value);
        this.updateOptionAvailability();
        this.handleChange();
        return;
      }

      const drawerButton = event.target.closest(".product-part-drawer-button");
      if (drawerButton) {
        const group = drawerButton.closest(".product-part-drawer-buttons");
        if (!group) return;

        this.selectDrawerButton(group, drawerButton.dataset.value);
        this.updateOptionAvailability();
        this.handleChange();
      }
    }

    selectSwatch(group, value, shouldDispatch = false) {
      const buttons = [...group.querySelectorAll(".product-part-swatch")];
      let matchedButton = null;

      buttons.forEach((button) => {
        const isSelected = button.dataset.value === value;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-selected", isSelected ? "true" : "false");

        if (isSelected) matchedButton = button;
      });

      if (shouldDispatch && matchedButton) {
        group.dispatchEvent(
          new CustomEvent("product-parts:swatch-change", {
            bubbles: true,
            detail: { value },
          }),
        );
      }
    }

    selectTypeSwatch(group, value) {
      const buttons = [...group.querySelectorAll(".product-part-type-swatch")];

      buttons.forEach((button) => {
        const isSelected = button.dataset.value === value;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-selected", isSelected ? "true" : "false");
      });
    }

    selectSizeButton(group, value) {
      const buttons = [...group.querySelectorAll(".product-part-size-button")];

      buttons.forEach((button) => {
        const isSelected = button.dataset.value === value;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-selected", isSelected ? "true" : "false");
      });
    }

    selectDrawerButton(group, value) {
      const buttons = [...group.querySelectorAll(".product-part-drawer-button")];

      buttons.forEach((button) => {
        const isSelected = button.dataset.value === value;
        button.classList.toggle("is-selected", isSelected);
        button.setAttribute("aria-selected", isSelected ? "true" : "false");
      });
    }

    getPartSelectedOptions(part) {
      const values = [];
      const selectors = [...part.querySelectorAll(".option-selector")];

      selectors.forEach((selector) => {
        const customSelect = selector.querySelector("custom-select");
        if (customSelect?.selectedOption?.dataset?.value) {
          values.push(customSelect.selectedOption.dataset.value);
          return;
        }

        const swatchGroup = selector.querySelector(".product-part-swatches");
        if (swatchGroup) {
          const selected = swatchGroup.querySelector(
            '.product-part-swatch[aria-selected="true"]',
          );
          if (selected?.dataset?.value) {
            values.push(selected.dataset.value);
          }
          return;
        }

        const typeGroup = selector.querySelector(".product-part-type-swatches");
        if (typeGroup) {
          const selected = typeGroup.querySelector(
            '.product-part-type-swatch[aria-selected="true"]',
          );
          if (selected?.dataset?.value) {
            values.push(selected.dataset.value);
          }
          return;
        }

        const sizeGroup = selector.querySelector(".product-part-size-buttons");
        if (sizeGroup) {
          const selected = sizeGroup.querySelector(
            '.product-part-size-button[aria-selected="true"]',
          );
          if (selected?.dataset?.value) {
            values.push(selected.dataset.value);
          }
          return;
        }

        const drawerGroup = selector.querySelector(".product-part-drawer-buttons");
        if (drawerGroup) {
          const selected = drawerGroup.querySelector(
            '.product-part-drawer-button[aria-selected="true"]',
          );
          if (selected?.dataset?.value) {
            values.push(selected.dataset.value);
          }
        }
      });

      return values;
    }

    updateOptionAvailability() {
      this.parts.forEach((part, partIndex) => {
        const partVariants = this.data.productParts[partIndex]?.variants || [];
        const selectors = [...part.querySelectorAll(".option-selector")];
        const selectedValues = this.getPartSelectedOptions(part);

        selectors.forEach((selector, optionIndex) => {
          const options = [...selector.querySelectorAll(".js-option")];

          options.forEach((optionEl) => {
            const testedValue = optionEl.dataset.value;

            const isAvailable = partVariants.some((variant) => {
              if (!variant.available) return false;

              return variant.options.every(
                (variantOptionValue, variantOptionIndex) => {
                  if (variantOptionIndex === optionIndex) {
                    return (
                      this.normalizeValue(variantOptionValue) ===
                      this.normalizeValue(testedValue)
                    );
                  }

                  const selectedValue = selectedValues[variantOptionIndex];
                  if (!selectedValue) return true;

                  return (
                    this.normalizeValue(variantOptionValue) ===
                    this.normalizeValue(selectedValue)
                  );
                },
              );
            });

            optionEl.dataset.available = isAvailable ? "true" : "false";

            optionEl.classList.toggle("is-sold-out", !isAvailable);
            optionEl.classList.toggle("is-unavailable", !isAvailable);

            if (!isAvailable) {
              optionEl.setAttribute("title", this.noStockText);
            } else {
              optionEl.removeAttribute("title");
            }
          });
        });
      });
    }

    handleChange(event) {
      if (event && event.target) {
        const part = event.target.closest(".product-part");
        if (part) {
          this.lastInteractedPartIndex = this.parts.indexOf(part);
        }
      }

      try {
        let skuParts = [];
        let partOptions = [];

        this.parts.forEach((part, n) => {
          const options = this.getPartSelectedOptions(part);
          partOptions.push(options);
          const partVariant = this.findPartVariantByOptions(n, options);

          if (partVariant && partVariant.sku) {
            skuParts.push(this.normalizeValue(partVariant.sku));
            if (this.dataset.isDuo === "true" && n === 1) {
              skuParts.push(this.normalizeValue(partVariant.sku));
            }
          }
        });

        const sku = skuParts.join("+");
        const normalizedSku = this.normalizeSku(sku);
        const matchingVariant = this.findMainVariantByModel(normalizedSku);

        if (matchingVariant && skuParts.length >= this.parts.length) {
          this.variant = matchingVariant;

          this.setAddToCartState(matchingVariant);
          this.updateOptionAvailability();
          this.updateSaleBadges();
          this.updateDisplayedPrice();
          this.onVariantChange();
          return;
        }

        let bestMatch = null;
        let bestScore = -1;

        this.mainModels.forEach((model) => {
          if (!model.variant?.id) return;

          let mainSkuParts = this.normalizeSku(model.name).split("+");
          let isValidCombination = true;
          let score = 0;

          this.parts.forEach((part, n) => {
            const partVariants = this.data.productParts[n]?.variants || [];
            const targetSku = mainSkuParts[n];
            const pVariant = partVariants.find(
              (v) => this.normalizeValue(v.sku) === targetSku,
            );

            if (!pVariant) {
              isValidCombination = false;
            } else {
              pVariant.options.forEach((opt, optIdx) => {
                const selectedOpt = partOptions[n] ? partOptions[n][optIdx] : null;

                if (
                  selectedOpt &&
                  this.normalizeValue(opt) === this.normalizeValue(selectedOpt)
                ) {
                  score += 1;

                  if (this.lastInteractedPartIndex === n) {
                    score += 50;
                  }
                }
              });
            }
          });

          if (this.dataset.isDuo === "true" && mainSkuParts.length < 3) {
            isValidCombination = false;
          }

          if (isValidCombination && score > bestScore) {
            bestScore = score;
            bestMatch = model.variant;
          }
        });

        if (bestMatch) {
          this.variant = bestMatch;

          this.setAddToCartState(bestMatch);
          this.initVariant();
        } else {
          throw new Error("Aucune combinaison valide trouvée.");
        }
      } catch (err) {
        console.warn("Erreur de sélection de variante:", err);
        this.updateOptionAvailability();
        this.updateSaleBadges();

        this.setAddToCartState(null);
      }
    }

    onVariantChange() {
      if (!this.variant) {
        console.warn("onVariantChange appelé sans variant");
        return;
      }

      this.updateVariantInputs();

      // L'URL ne suit la variante que sur la fiche produit elle-même. Ailleurs
      // — pack builder, page de recommandation — ce sélecteur est chargé par la
      // Section Rendering API depuis l'URL du produit : y recopier cette URL
      // effacerait celle de la page en cours (et ses paramètres) au premier
      // clic sur une option.
      const productUrl = new URL(this.dataset.url, window.location.origin);

      if (productUrl.pathname === window.location.pathname) {
        // On repart de l'URL courante plutôt que du seul chemin produit : elle
        // porte parfois un fragment d'affiliation (#…) ou des paramètres de
        // campagne, que reconstruire l'URL de zéro effacerait. Cette
        // réécriture-ci part dès le chargement (initVariant), donc avant que
        // les tags tiers chargés en async aient eu le temps de les lire.
        const url = new URL(window.location.href);
        url.searchParams.set("variant", this.variant.id);

        window.history.replaceState({}, "", url);
      }

      const detail = this.getVariantEventDetail();

      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail,
        }),
      );

      document.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail,
        }),
      );

      window.dispatchEvent(
        new CustomEvent("on:variant:change", {
          detail,
        }),
      );
    }
  }

  customElements.define("product-parts", ProductParts);
}
